import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import selectors from '../../../modules/selectors/datePickerSelectors';
import { TIME_PERIOD, WEEK_SUBSTRACTION_VALUE } from '../../../constants/datePickerConstants';
import api from '../../../services/api';
import {
  SUB_BRANCH_OVERALL_COMPARISON_SPEND_TABLE,
  SUB_BRANCH_OVERALL_PERFORMANCE_FULL_YEAR,
  SUB_BRANCH_PERC_OPPS_TO_PLAN,
} from '../constants/PerformanceOverviewConstants';
import {
  combineData,
  transformOppsToPlantData,
  generateOppsToPlantColumns,
} from '../services/PerformanceOverviewSubBranchServices';
import { COMPONENT_QUERY, COMPONENT_QUERY_COMPARE } from '../../../constants/api';
import { previousWeekExtractor } from '../../../data/branchPerformanceData';
import { TransformDate } from '../../../components/DatePicker/DatePickerUtils/datePickerUtils';
import { currentYearAndQuarterFun, getFilteredStoreOptions } from '../../../constants/utils';
import { performanceOverviewSubBranchActions } from '../../../modules/reducer/performanceOverviewSubBranch';

export const usePerformanceOverviewSubBranchHook = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { userStores, userData } = useSelector((state) => state.auth);
  const { transformedDataWithDate } = useSelector(selectors.datePickerCombinedData);
  const { performanceOverviewSubBranchRange } = useSelector(
    (state) => state.performanceOverviewSubBranch
  );

  // States for Table 1
  const [defaultFilter, setDefaultFilter] = useState(TIME_PERIOD);
  const [timePeriodFilter, setTimePeriodFilter] = useState([]);
  const [downloadFilters, setDownloadFilters] = useState({}); // For Table 1
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isReportDownloading, setIsReportDownloading] = useState(false);

  // States for Table 2 - Opps to Plan
  const [oppsToPlantData, setOppsToPlantData] = useState([]);
  const [oppsToPlantColumns, setOppsToPlantColumns] = useState([]);
  const [isOppsToPlantLoading, setIsOppsToPlantLoading] = useState(true);
  const [isOppsToPlantError, setIsOppsToPlantError] = useState(false);
  const [oppsToPlantFilter, setOppsToPlantFilter] = useState([]);
  const [oppsDownloadFilters, setOppsDownloadFilters] = useState({}); // For Table 2
  const [isOppsToPlantDownloading, setIsOppsToPlantDownloading] = useState(false);

  // Separate states for tracking filter readiness
  const [table1FiltersReady, setTable1FiltersReady] = useState(false);
  const [table2FiltersReady, setTable2FiltersReady] = useState(false);

  // Refs
  const initialLoading = useRef(true);
  const rangeInitialized = useRef(false);
  const lastRangeUsed = useRef(null);
  const lastTable2FilterString = useRef(null);

  // Get filtered store options
  const options = useMemo(
    () => getFilteredStoreOptions(userStores, userData?.data, true, location.pathname),
    [userStores, userData, location.pathname]
  );

  // Get all store IDs from filtered options
  const filteredStoreIds = useMemo(() => options.map((option) => option.value), [options]);

  // Format week number with leading zero if needed
  const formatWeekNumber = useCallback((week) => (week <= 9 ? `0${week}` : `${week}`), []);

  // Create date index from year and week
  const createDateIndex = useCallback(
    (year, week) => `${year}${formatWeekNumber(week)}`,
    [formatWeekNumber]
  );

  // Check if an object has null values
  const checkForGlobalObject = useCallback((object) => {
    if (object === null || object === undefined) {
      return true;
    }
    return (
      Object.values(object).every((value) => value === null || value === undefined) ||
      Object.values(object).some((value) => value === null || value === undefined)
    );
  }, []);

  // Handle time period range initialization
  const timePeriodRangeHandler = useCallback(() => {
    const obj = TransformDate?.getCurrentYearAndQuarter(transformedDataWithDate);

    if (obj && Object.keys(obj).length && transformedDataWithDate?.length > 0) {
      const quarterValue = TransformDate?.quickFilters?.TWELVE_WEEK_DATA(
        transformedDataWithDate,
        previousWeekExtractor,
        WEEK_SUBSTRACTION_VALUE
      );

      if (quarterValue) {
        dispatch(
          performanceOverviewSubBranchActions.setPerformanceOverviewSubBranchRange(quarterValue)
        );

        if (
          quarterValue?.start_week &&
          quarterValue?.start_year &&
          quarterValue?.end_week &&
          quarterValue?.end_year
        ) {
          setDefaultFilter(
            `w${quarterValue.start_week}, ${quarterValue.start_year} - w${quarterValue.end_week}, ${quarterValue.end_year}`
          );
        }
      }
    }
  }, [dispatch, transformedDataWithDate]);

  // Handle quarter and year value initialization
  const quarterAndYearValueHandler = useCallback(() => {
    if (transformedDataWithDate?.length > 0) {
      const currentYearAndQuarter = currentYearAndQuarterFun(
        transformedDataWithDate,
        previousWeekExtractor,
        WEEK_SUBSTRACTION_VALUE,
        TransformDate
      );

      if (currentYearAndQuarter) {
        dispatch(
          performanceOverviewSubBranchActions.setCurrentQuarter(
            currentYearAndQuarter?.currentQuarterValue
          )
        );

        dispatch(
          performanceOverviewSubBranchActions.setCurrentYear(
            currentYearAndQuarter?.currentYearBasedOnWeek
          )
        );
      }
    }
  }, [dispatch, transformedDataWithDate]);

  // Create filter array with date and store conditions
  const createFilterArray = useCallback(
    (startYear, startWeek, endYear, endWeek, storeIds) => [
      {
        col: 'dateindex',
        op: '>=',
        val: createDateIndex(startYear, startWeek),
      },
      {
        col: 'dateindex',
        op: '<=',
        val: createDateIndex(endYear, endWeek),
      },
      {
        col: 'store_id',
        op: 'in',
        val: storeIds,
      },
    ],
    [createDateIndex]
  );

  // Create filter structure for comparison data
  const createComparisonFilters = useCallback(
    (startYear, startWeek, endYear, endWeek, storeIds) => {
      const currentFilters = createFilterArray(endYear, startWeek, endYear, endWeek, storeIds);
      const previousFilters = createFilterArray(
        endYear - 1,
        startWeek,
        endYear - 1,
        endWeek,
        storeIds
      );

      return {
        current_query_builder: { filters: currentFilters },
        previous_query_builder: { filters: previousFilters },
        join_columns: ['store_id'],
      };
    },
    [createFilterArray]
  );

  // Create Table 2 (Opps to Plan) API filters - from week 01 of current year to current week
  const createOppsToPlantFilters = useCallback(
    (storeIds) => {
      if (!transformedDataWithDate?.length) {
        return [];
      }

      // Use previousWeekExtractor with WEEK_SUBSTRACTION_VALUE to get the current week data
      const weekData = previousWeekExtractor(transformedDataWithDate, WEEK_SUBSTRACTION_VALUE);

      if (!weekData?.currentYear || !weekData?.currentWeek) {
        return [];
      }

      const currentYear = weekData.currentYear;
      const currentWeek = weekData.currentWeek;

      // Create filters from week 01 to current week of the current year
      // Format: store_id first (for consistency), then date filters
      const filters = [
        {
          col: 'store_id',
          op: 'in',
          val: storeIds,
        },
        {
          col: 'dateindex',
          op: '>=',
          val: `${currentYear}01`, // Always start from week 01
        },
        {
          col: 'dateindex',
          op: '<=',
          val: createDateIndex(currentYear, currentWeek),
        },
      ];

      return filters;
    },
    [transformedDataWithDate, createDateIndex]
  );

  // Create Table 2 download filters (uppercase IN operator)
  const createOppsToPlantDownloadFilters = useCallback(
    (storeIds) => {
      if (!transformedDataWithDate?.length) {
        return [];
      }

      const weekData = previousWeekExtractor(transformedDataWithDate, WEEK_SUBSTRACTION_VALUE);

      if (!weekData?.currentYear || !weekData?.currentWeek) {
        return [];
      }

      const currentYear = weekData.currentYear;
      const currentWeek = weekData.currentWeek;

      // For download, store_id filter comes first with uppercase IN
      const filters = [
        {
          col: 'store_id',
          op: 'IN', // Uppercase for download
          val: storeIds,
        },
        {
          col: 'dateindex',
          op: '>=',
          val: `${currentYear}01`,
        },
        {
          col: 'dateindex',
          op: '<=',
          val: createDateIndex(currentYear, currentWeek),
        },
      ];

      return filters;
    },
    [transformedDataWithDate, createDateIndex]
  );

  // Initialize time period range
  useEffect(() => {
    if (!rangeInitialized.current && transformedDataWithDate?.length > 0) {
      const allValuesAreNull = checkForGlobalObject(performanceOverviewSubBranchRange);

      if (allValuesAreNull) {
        timePeriodRangeHandler();
        quarterAndYearValueHandler();
        rangeInitialized.current = true;
      }
    }
  }, [
    transformedDataWithDate,
    checkForGlobalObject,
    timePeriodRangeHandler,
    quarterAndYearValueHandler,
    performanceOverviewSubBranchRange,
  ]);

  // Update TABLE 1 filters when date range changes
  useEffect(() => {
    const { start_week, start_year, end_week, end_year } = performanceOverviewSubBranchRange || {};

    if (
      start_week !== null &&
      start_year !== null &&
      end_week !== null &&
      end_year !== null &&
      filteredStoreIds.length > 0
    ) {
      const currentRangeString = JSON.stringify({
        start_week,
        start_year,
        end_week,
        end_year,
      });
      const isRangeChanged = lastRangeUsed.current !== currentRangeString;

      if (isRangeChanged) {
        // Table 1 filters
        const newTimePeriodFilter = createFilterArray(
          start_year,
          start_week,
          end_year,
          end_week,
          filteredStoreIds
        );

        const comparisonFilters = createComparisonFilters(
          start_year,
          start_week,
          end_year,
          end_week,
          filteredStoreIds
        );

        setTimePeriodFilter(newTimePeriodFilter);
        setTable1FiltersReady(true);

        // Update download filters ONLY for Table 1
        const table1DownloadFilters = {
          menu_name: 'sub_branch_performance_overview',
          [SUB_BRANCH_OVERALL_COMPARISON_SPEND_TABLE]: comparisonFilters,
          [SUB_BRANCH_OVERALL_PERFORMANCE_FULL_YEAR]: {
            filters: newTimePeriodFilter,
          },
        };

        setDownloadFilters(table1DownloadFilters);
        lastRangeUsed.current = currentRangeString;
      }
    }
  }, [
    performanceOverviewSubBranchRange?.start_week,
    performanceOverviewSubBranchRange?.start_year,
    performanceOverviewSubBranchRange?.end_week,
    performanceOverviewSubBranchRange?.end_year,
    filteredStoreIds.length,
    createFilterArray,
    createComparisonFilters,
  ]);

  // Create TABLE 2 filters independently
  useEffect(() => {
    if (transformedDataWithDate?.length > 0 && filteredStoreIds.length > 0) {
      // Create filters using the helper function
      const oppsFilters = createOppsToPlantFilters(filteredStoreIds);
      const oppsDownloadFiltersData = createOppsToPlantDownloadFilters(filteredStoreIds);

      if (oppsFilters.length > 0) {
        const filterString = JSON.stringify(oppsFilters);
        const hasFilterChanged = lastTable2FilterString.current !== filterString;

        if (hasFilterChanged) {
          setOppsToPlantFilter(oppsFilters);
          setTable2FiltersReady(true);
          lastTable2FilterString.current = filterString;

          // Update download filters ONLY for Table 2
          const table2DownloadFilters = {
            [SUB_BRANCH_PERC_OPPS_TO_PLAN]: {
              filters: oppsDownloadFiltersData,
            },
          };

          setOppsDownloadFilters(table2DownloadFilters);
        }
      }
    }
  }, [
    transformedDataWithDate,
    filteredStoreIds,
    createOppsToPlantFilters,
    createOppsToPlantDownloadFilters,
  ]);

  // TABLE 1 API CALLS
  useEffect(() => {
    const fetchTable1Data = async () => {
      if (!table1FiltersReady || filteredStoreIds.length === 0 || timePeriodFilter.length === 0) {
        return;
      }

      setTable1FiltersReady(false);
      setIsLoading(true);
      setIsError(false);
      initialLoading.current = false;

      try {
        // Fetch plan data
        const planResponse = await api.post(
          `${COMPONENT_QUERY}?component=${SUB_BRANCH_OVERALL_PERFORMANCE_FULL_YEAR}`,
          {
            [SUB_BRANCH_OVERALL_PERFORMANCE_FULL_YEAR]: {
              filters: timePeriodFilter,
            },
          }
        );
        const planData =
          planResponse.data?.data?.[SUB_BRANCH_OVERALL_PERFORMANCE_FULL_YEAR]?.result || [];

        // Fetch comparison data
        const { start_week, start_year, end_week, end_year } =
          performanceOverviewSubBranchRange || {};
        let comparisonData = [];

        if (start_week && start_year && end_week && end_year) {
          const comparisonFilters = createComparisonFilters(
            start_year,
            start_week,
            end_year,
            end_week,
            filteredStoreIds
          );

          const comparisonResponse = await api.post(
            `${COMPONENT_QUERY_COMPARE}?component=${SUB_BRANCH_OVERALL_COMPARISON_SPEND_TABLE}`,
            comparisonFilters
          );

          comparisonData = comparisonResponse.data?.data || [];
        }

        // Process and combine data
        const combinedData = combineData(planData, comparisonData, userStores || []);

        setTableData(combinedData);
      } catch (error) {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTable1Data();
  }, [table1FiltersReady]);

  // TABLE 2 API CALL - COMPLETELY INDEPENDENT
  useEffect(() => {
    const fetchTable2Data = async () => {
      if (!table2FiltersReady || oppsToPlantFilter.length === 0) {
        return;
      }
      setTable2FiltersReady(false);
      setIsOppsToPlantLoading(true);
      setIsOppsToPlantError(false);

      try {
        const payload = {
          [SUB_BRANCH_PERC_OPPS_TO_PLAN]: {
            filters: oppsToPlantFilter,
          },
        };

        const response = await api.post(
          `${COMPONENT_QUERY}?component=${SUB_BRANCH_PERC_OPPS_TO_PLAN}`,
          payload
        );

        const oppsData = response.data?.data?.[SUB_BRANCH_PERC_OPPS_TO_PLAN]?.result || [];

        // Transform the data
        const transformedOppsData = transformOppsToPlantData(oppsData, userStores || []);
        const columns = generateOppsToPlantColumns(transformedOppsData);

        setOppsToPlantData(transformedOppsData);
        setOppsToPlantColumns(columns);
      } catch (error) {
        setIsOppsToPlantError(true);
      } finally {
        setIsOppsToPlantLoading(false);
      }
    };

    fetchTable2Data();
  }, [table2FiltersReady]);

  return {
    userStores,
    userData,
    options,
    defaultFilter,
    tableData,
    isLoading,
    isError,
    isReportDownloading,
    setIsReportDownloading,
    downloadFilters, // Table 1 download filters
    initialLoading: initialLoading.current,

    // Table 2 - Opps to Plan
    oppsToPlantData,
    oppsToPlantColumns,
    isOppsToPlantLoading,
    isOppsToPlantError,
    isOppsToPlantDownloading,
    setIsOppsToPlantDownloading,
    oppsDownloadFilters, // Table 2 download filters - SEPARATE
  };
};
